import { skipToken } from "@apollo/client";
import { useSuspenseQueryHandler } from "@sinch/hooks/useSuspenseQueryHandler";
import type { ConditionCategory, ConditionType } from "@sinch/types/sinch.types";
import { RateConditionPossibleValues } from "./queries.graphql";
import type { RateConditionPossibleValuesQuery, RateConditionPossibleValuesQueryVariables } from "./queries.types";
import { match } from "ts-pattern";
import { ConditionValueType as ConditionValueTypeEnum } from "@sinch/core/entities/serverEnums";
import { ConditionValueType } from "@sinch/core/entities/serverEnums.types";
import { ensureArray } from "ramda-adjunct";
import { NationalHolidayConditionFormFields } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionValue/nationalHolidayFormSchema";
import { WeekDaySchema } from "@sinch/components/application/TimeIntervalSettings/formSchema";
import { isEmpty, propEq } from "ramda";
import { join } from "@sinch/utils/string";
import { getWeekdayName } from "@sinch/utils/dateTime/format";
import { useRateConditions } from "@sinch/components/application/RateCondition/useRateConditions";
import { useTranslation } from "react-i18next";
import { useFormat } from "@sinch/core/hooks/format";
import { useSettings } from "@sinch/core/providers/AppSettings";

interface UseRateConditionPossibleValuesArgs {
  subjectId?: string | null;
  category: ConditionCategory | null | undefined;
  type: ConditionType | null | undefined;
}

/**
 * Loads possible values for a rate condition. Skips fetching when category or type is null.
 * `subjectId` is nullable in the schema, so a null value is passed through and does not skip fetching.
 */
export const useRateConditionPossibleValues = (condition?: UseRateConditionPossibleValuesArgs | null) => {
  const { t } = useTranslation();
  const { list, date, time } = useFormat();
  const { language } = useSettings();

  const shouldFetch = condition && condition?.category != null && condition?.type != null;

  const { data } = useSuspenseQueryHandler<RateConditionPossibleValuesQuery, RateConditionPossibleValuesQueryVariables>(
    RateConditionPossibleValues,
    shouldFetch
      ? { subjectId: condition?.subjectId ?? null, category: condition.category!, type: condition.type! }
      : undefined,
    shouldFetch ? {} : skipToken
  );

  const { getValueType } = useRateConditions(condition?.category);

  const describeTimes = (times: WeekDaySchema["times"] | undefined) =>
    isEmpty(times ?? [])
      ? t("RateUnit.entireDay")
      : join(
          (times ?? []).map(({ from, to }) => join([from && time.short(from), to && time.short(to)], " - ")),
          ", "
        );

  const describeCountry = (country: string | null | undefined) =>
    country === null || country === undefined
      ? ""
      : (new Intl.DisplayNames([language], { type: "region" }).of(country) ?? country);

  const getValueLabel = (valueVal: any) => {
    if (!condition?.type) {
      return "";
    }
    const valueType = getValueType(condition?.subjectId, condition?.type);

    return match<{ valueType: ConditionValueType | null; value: any }>({ valueType, value: valueVal })
      .with({ valueType: ConditionValueTypeEnum.Select }, ({ value }) => {
        const values = ensureArray(value)
          .map((val) => data?.rateConditionPossibleValues.find(({ value }) => val === value)?.label)
          .filter(Boolean);
        return list.or(values as string[]);
      })
      .with({ valueType: ConditionValueTypeEnum.Number }, ({ value }) => value)
      .with({ valueType: ConditionValueTypeEnum.Date }, ({ value }) => date.medium(value))
      .with(
        { valueType: ConditionValueTypeEnum.WeekdayTimes },
        ({ value: weekdays }: { value: { daysOfWeek: WeekDaySchema[] } }) =>
          weekdays?.daysOfWeek
            ?.filter(propEq("enabled", true))
            .map(({ weekDay, times }) => join([getWeekdayName(weekDay, language, "short"), describeTimes(times)], ": "))
      )
      .with(
        { valueType: ConditionValueTypeEnum.NationalHolidayTimes },
        ({ value: holidays }: { value: NationalHolidayConditionFormFields }) =>
          join([describeCountry(holidays?.country), describeTimes(holidays?.times)], ": ")
      )
      .otherwise(({ value }) => value);
  };

  return { rateConditionPossibleValues: data?.rateConditionPossibleValues ?? null, getValueLabel };
};
